import { useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";

const useUserAnalytics = (users) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("lastVisited");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [copyFeedback, setCopyFeedback] = useState(false);

  // Helper functions
  const getCompletionRate = useCallback((created, completed) => {
    // Handle null, undefined, or non-numeric values
    const createdNum = Number(created) || 0;
    const completedNum = Number(completed) || 0;

    if (createdNum === 0) return 0;
    return Math.round((completedNum / createdNum) * 100);
  }, []);

  const getCompletionRateForSort = useCallback(
    (user) => {
      return getCompletionRate(user.tasksCreated, user.tasksCompleted);
    },
    [getCompletionRate],
  );

  const getActivityStatus = useCallback((lastVisited) => {
    const daysSinceLastVisit = dayjs().diff(dayjs(lastVisited), "day");
    if (daysSinceLastVisit <= 1) return { label: "Active", color: "success" };
    if (daysSinceLastVisit <= 7) return { label: "Recent", color: "warning" };
    if (daysSinceLastVisit <= 30) return { label: "Inactive", color: "error" };
    return { label: "Dormant", color: "default" };
  }, []);

  // Copy user ID to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(true);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  // Filtered and sorted users
  const processedUsers = useMemo(() => {
    let filtered = users.filter((user) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        user.userId?.toLowerCase().includes(searchLower) ||
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
      );
    });

    // Sort users
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Special handling for completion rate
      if (sortBy === "tasksCompleted") {
        aValue = getCompletionRateForSort(a);
        bValue = getCompletionRateForSort(b);
      }

      // Handle null/undefined values with appropriate defaults
      if (aValue == null) {
        aValue =
          sortBy === "lastVisited" ||
          sortBy === "totalVisits" ||
          sortBy === "tasksCreated" ||
          sortBy === "tasksCompleted"
            ? 0
            : "";
      }
      if (bValue == null) {
        bValue =
          sortBy === "lastVisited" ||
          sortBy === "totalVisits" ||
          sortBy === "tasksCreated" ||
          sortBy === "tasksCompleted"
            ? 0
            : "";
      }

      // Determine if we're dealing with numeric or string comparison
      const isNumericSort = [
        "lastVisited",
        "totalVisits",
        "tasksCreated",
        "tasksCompleted",
      ].includes(sortBy);

      if (isNumericSort) {
        // Numeric comparison
        const numA = Number(aValue) || 0;
        const numB = Number(bValue) || 0;

        if (sortOrder === "asc") {
          return numA - numB;
        } else {
          return numB - numA;
        }
      } else {
        // String comparison for text fields (name, email, userId)
        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();

        if (sortOrder === "asc") {
          return strA.localeCompare(strB);
        } else {
          return strB.localeCompare(strA);
        }
      }
    });

    return filtered;
  }, [users, searchTerm, sortBy, sortOrder, getCompletionRateForSort]);

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return processedUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [processedUsers, page, rowsPerPage]);

  // Handle sort
  const handleSort = (property) => {
    if (sortBy === property) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(property);
      setSortOrder("desc");
    }
    setPage(1); // Reset to first page when sorting
  };

  return {
    // State
    searchTerm,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    copyFeedback,
    // Data
    processedUsers,
    paginatedUsers,
    // Handlers
    setSearchTerm: (value) => {
      setSearchTerm(value);
      setPage(1);
    },
    setRowsPerPage: (value) => {
      setRowsPerPage(value);
      setPage(1);
    },
    setPage,
    setCopyFeedback,
    handleSort,
    copyToClipboard,
    // Helper functions
    getCompletionRate,
    getActivityStatus,
  };
};

export default useUserAnalytics;
